import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../lib/auth/auth-context';
import { supabase as defaultClient } from '../lib/supabase';
import { captureSupabaseError } from '../lib/sentry-supabase';
import type { LynrxSupabaseClient } from '@lynrx/shared';
import { Alert } from '../components/ui/Alert';
import { Button } from '../components/ui/Button';
import {
  AuditEventList,
  type AuditEventRow,
} from '../components/audit/AuditEventList';
import {
  ENTITY_TYPE_OPTIONS,
  EVENT_TYPE_GROUPS,
  humanizeEventType,
} from '../components/audit/event-labels';
import { useSetPageHeader } from '../components/layout/PageHeaderContext';

// Tenant-wide audit browse (v1). RLS-scoped via the browser client —
// the audit_events_select_tenant_or_pharmacy_or_admin policy handles
// tenant isolation, so application code only needs to filter on the
// caller-controlled fields (entity_type, event_type, occurred_at
// range). Newest-first, paginated, peek-for-next-page like Patients.
//
// Out of scope for v1 (tracked as follow-ups, not built here):
//   - Free-text JSONB-payload search — needs GIN/tsvector + a review
//     of what payload content should be searchable given PHI adjacency.
//   - Decision-22 pharmacy-admin event-type display config — belongs
//     with the Admin/pharmacy settings work. v1 shows everything.
//   - One-click PDF export — blocked behind the POD epic.

const PAGE_SIZE = 25;

// Embeds the actor's display fields from public.users via the
// audit_events_actor_user_id_fkey FK — same shape used by the
// order-detail + driver-detail per-entity timelines.
const AUDIT_LIST_COLUMNS =
  'id, occurred_at, event_type, entity_type, entity_id, actor_user_id, actor_type, payload, actor_user:users!audit_events_actor_user_id_fkey(full_name, email)';

// Bracket a local YYYY-MM-DD date into a tz-aware ISO timestamp for
// occurred_at comparisons. `endOfDay` widens to 23:59:59.999 local so
// "Apr 5 → Apr 5" includes the whole day.
function localDateToIso(yyyyMmDd: string, endOfDay: boolean): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(yyyyMmDd)) return null;
  const [y, m, d] = yyyyMmDd.split('-').map(Number) as [number, number, number];
  const local = endOfDay
    ? new Date(y, m - 1, d, 23, 59, 59, 999)
    : new Date(y, m - 1, d, 0, 0, 0, 0);
  if (Number.isNaN(local.getTime())) return null;
  return local.toISOString();
}

export const Route = createFileRoute('/_authenticated/audit/')({
  component: AuditBrowsePage,
});

function AuditBrowsePage(): JSX.Element {
  return <AuditBrowseView />;
}

type ViewProps = {
  // Test seam — defaults to the singleton browser supabase client.
  client?: LynrxSupabaseClient;
};

export function AuditBrowseView({
  client = defaultClient,
}: ViewProps): JSX.Element {
  const { session, isReady } = useAuth();
  useSetPageHeader({
    title: 'Audit log',
    subtitle:
      'Tenant-wide audit trail across orders, routes, deliveries, and admin actions.',
  });

  const [entityType, setEntityType] = useState<string>('');
  const [eventType, setEventType] = useState<string>('');
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');

  const [rows, setRows] = useState<AuditEventRow[] | null>(null);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPage = useCallback(
    async (nextPage: number) => {
      if (!session) return;
      setLoading(true);
      setError(null);
      const from = nextPage * PAGE_SIZE;
      const to = from + PAGE_SIZE; // peek one row past page-end

      // Build the query progressively so each filter only narrows when
      // set. RLS keeps tenant isolation; we never filter on tenant_id
      // from the client.
      let query = client
        .from('audit_events')
        .select(AUDIT_LIST_COLUMNS)
        .order('occurred_at', { ascending: false });

      if (entityType) query = query.eq('entity_type', entityType);
      if (eventType) query = query.eq('event_type', eventType);
      const fromIso = fromDate ? localDateToIso(fromDate, false) : null;
      const toIso = toDate ? localDateToIso(toDate, true) : null;
      if (fromIso) query = query.gte('occurred_at', fromIso);
      if (toIso) query = query.lte('occurred_at', toIso);

      const { data, error: err } = await query.range(from, to);
      setLoading(false);
      if (err) {
        captureSupabaseError(err, 'audit.list.fetch');
        setError('Could not load audit events.');
        return;
      }
      const all = (data ?? []) as unknown as AuditEventRow[];
      setRows(all.slice(0, PAGE_SIZE));
      setHasMore(all.length > PAGE_SIZE);
      setPage(nextPage);
    },
    [client, session, entityType, eventType, fromDate, toDate],
  );

  // Re-fetch from page 0 whenever a filter changes or the session
  // becomes ready. fetchPage's identity changes when filters change,
  // so this effect re-runs.
  useEffect(() => {
    if (!isReady || !session) return;
    void fetchPage(0);
  }, [isReady, session, fetchPage]);

  const eventLabel = useMemo(() => humanizeEventType, []);

  function resetFilters(): void {
    setEntityType('');
    setEventType('');
    setFromDate('');
    setToDate('');
  }

  const filtersActive =
    Boolean(entityType) || Boolean(eventType) || Boolean(fromDate) || Boolean(toDate);

  return (
    <>
      {error ? (
        <div className="mb-4">
          <Alert tone="error">{error}</Alert>
        </div>
      ) : null}

      <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <div className="flex flex-col gap-1">
          <label htmlFor="audit-entity-type" className="text-xs font-medium text-ink-2">
            Entity type
          </label>
          <select
            id="audit-entity-type"
            value={entityType}
            onChange={(e) => setEntityType(e.target.value)}
            className="rounded-md border border-border bg-bg px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="">All</option>
            {ENTITY_TYPE_OPTIONS.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="audit-event-type" className="text-xs font-medium text-ink-2">
            Event type
          </label>
          <select
            id="audit-event-type"
            value={eventType}
            onChange={(e) => setEventType(e.target.value)}
            className="rounded-md border border-border bg-bg px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="">All</option>
            {EVENT_TYPE_GROUPS.map((g) => (
              <optgroup key={g.label} label={g.label}>
                {g.values.map((v) => (
                  <option key={v} value={v}>
                    {humanizeEventType(v)}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="audit-from-date" className="text-xs font-medium text-ink-2">
            From
          </label>
          <input
            id="audit-from-date"
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="rounded-md border border-border bg-bg px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="audit-to-date" className="text-xs font-medium text-ink-2">
            To
          </label>
          <input
            id="audit-to-date"
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="rounded-md border border-border bg-bg px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        <div className="flex items-end">
          <Button
            variant="ghost"
            onClick={resetFilters}
            disabled={!filtersActive || loading}
          >
            Reset filters
          </Button>
        </div>
      </div>

      {rows === null ? (
        <p className="text-sm text-ink-2">Loading audit events…</p>
      ) : (
        <AuditEventList
          rows={rows}
          eventLabel={eventLabel}
          emptyMessage={
            filtersActive
              ? 'No audit events match these filters.'
              : 'No audit events yet.'
          }
        />
      )}

      <div className="mt-4 flex items-center justify-between text-sm text-ink-2">
        <span>
          {rows && rows.length > 0
            ? `Page ${page + 1} · showing ${rows.length} event${rows.length === 1 ? '' : 's'}`
            : ''}
        </span>
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            onClick={() => void fetchPage(page - 1)}
            disabled={page === 0 || loading}
          >
            Previous
          </Button>
          <Button
            variant="secondary"
            onClick={() => void fetchPage(page + 1)}
            disabled={!hasMore || loading}
          >
            Next
          </Button>
        </div>
      </div>
    </>
  );
}
